// client/lib/wp.ts

// ---------- Types ----------
export type WpRendered = { rendered: string };

export type WpMedia = {
  id: number;
  source_url?: string;
  media_details?: {
    sizes?: Record<string, { source_url: string }>;
  };
};

type WpSeoFields = {
  link?: string;
  aioseo_head?: string | null;
  aioseo_head_json?: unknown;
};

export type WpPost = WpSeoFields & {
  id: number;
  slug: string;
  date: string;
  title: WpRendered;
  excerpt: WpRendered;
  content: WpRendered;
  _embedded?: {
    ["wp:featuredmedia"]?: WpMedia[];
    author?: Array<{ name: string }>;
    ["wp:term"]?: Array<Array<{ id: number; name: string; slug: string }>>;
  };
};

export type WpPage = WpSeoFields & {
  id: number;
  slug: string;
  date: string;
  title: WpRendered;
  content: WpRendered;
  _embedded?: WpPost["_embedded"];
  acf?: Record<string, unknown>;
};

export type MenuNode = {
  id: number;
  objectId: number;
  object: string;
  title: string;
  url: string;
  description?: string | null;
  classes?: string[] | null;
  icon?: string | null;
  iconColor?: string | null;
  mobileOnly?: boolean;
  children?: MenuNode[];
};

// ---------- Base URL resolution ----------
const injected =
  (typeof window !== "undefined" && (window as any).__PRERENDER_WP_BASE__) || null;

const envBaseRaw = (import.meta.env.VITE_WP_URL || "").trim().replace(/\/$/, "");
const envBase = envBaseRaw || "";

// During development, if we're requesting French content, bypass the proxy
// and go directly to the WordPress site to avoid proxy language issues
const wpBase = injected || envBase || ""; // prefer injected > env > relative

const API = `${wpBase}/wp-json/wp/v2`;
const ONPLEXUS_API = `${wpBase}/wp-json/onplexus/v1`;
const isAbsolute = /^https?:\/\//i.test(wpBase);
const defaultCreds: RequestCredentials = isAbsolute ? "omit" : "same-origin";
const DEFAULT_LANG = (import.meta.env.VITE_DEFAULT_LANG || "en").toLowerCase();
const WP_ORIGIN = (() => {
  if (!wpBase || !/^https?:\/\//i.test(wpBase)) return null;
  try {
    return new URL(wpBase).origin;
  } catch {
    return null;
  }
})();

// ---------- Request de-dupe & tiny TTL cache ----------
type CacheEntry<T> = { at: number; data: T };
const IN_FLIGHT = new Map<string, Promise<any>>();
const SOFT_CACHE = new Map<string, CacheEntry<any>>();
const TTL_MS = 5000; // 5s is enough to collapse StrictMode/double mounts + quick nav

// Clear cache when language changes to prevent serving wrong language content
export function clearLanguageCache(lang?: string) {
  if (lang) {
    // Clear all entries that might contain language-specific content
    const keysToDelete: string[] = [];
    for (const key of SOFT_CACHE.keys()) {
      if (key.includes(`lang=${lang}`) || key.includes(`/pages/`)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => SOFT_CACHE.delete(key));
  } else {
    // Clear all cache if no specific language
    SOFT_CACHE.clear();
  }
  IN_FLIGHT.clear();
}

function fromCache<T>(key: string): T | null {
  const hit = SOFT_CACHE.get(key);
  if (!hit) return null;
  if (Date.now() - hit.at > TTL_MS) {
    SOFT_CACHE.delete(key);
    return null;
  }
  return hit.data as T;
}
function putCache<T>(key: string, data: T) {
  // Don't cache if the URL contains language parameters to avoid cross-language pollution
  // Only cache non-language-specific requests
  if (!key.includes('lang=') && !key.includes('/pages/')) {
    SOFT_CACHE.set(key, { at: Date.now(), data });
  }
}

async function fetchJsonRaw(url: string, init?: RequestInit) {
  const res = await fetch(url, { credentials: defaultCreds, ...init });
  const contentType = res.headers.get("content-type") || "";
  if (!res.ok || !contentType.includes("json")) {
    const body = await res.text().catch(() => "");
    const snippet = body.slice(0, 300);
    throw new Error(
      `WP error ${res.status} ${res.statusText} for ${url}\n` +
        (snippet ? `---\n${snippet}\n---` : "")
    );
  }
  return { data: await res.json(), res };
}

async function fetchJson<T = unknown>(url: string, init?: RequestInit): Promise<{ data: T; res: Response }> {
  // Only add cache-busting if not already present
  let finalUrl = url;
  if (!url.includes('_t=') && !url.includes('_nocache=') && !url.includes('_cb=')) {
    // Add cache-busting parameter to prevent nginx/Cloudflare caching issues
    finalUrl = url.includes('?')
      ? url + '&_cb=' + Date.now()
      : url + '?_cb=' + Date.now();
  }

  // Skip local cache for language-specific requests
  const skipCache = url.includes('lang=') || url.includes('/pages/');

  if (!skipCache) {
    const cached = fromCache<{ data: T; res: Response }>(finalUrl);
    if (cached) return cached;
  }

  // collapse in-flight duplicates
  const inflight = IN_FLIGHT.get(finalUrl);
  if (inflight) return inflight as Promise<{ data: T; res: Response }>;

  const p = fetchJsonRaw(finalUrl, init)
    .then(({ data, res }) => {
      const out = { data: data as T, res };
      if (!skipCache) {
        putCache(finalUrl, out);
      }
      return out;
    })
    .finally(() => {
      IN_FLIGHT.delete(finalUrl);
    });

  IN_FLIGHT.set(finalUrl, p);
  return p;
}

// ---------- Public API ----------
export async function getPosts(opts?: { page?: number; perPage?: number }) {
  return getPostsByLang(undefined, opts);
}

export async function getPostsByLang(lang?: string, opts?: { page?: number; perPage?: number }) {
  const page = opts?.page ?? 1;
  const perPage = opts?.perPage ?? 10;
  const qs = new URLSearchParams({
    per_page: String(perPage),
    page: String(page),
    _embed: "1",
  });
  if (lang) {
    qs.set("lang", lang);
  }
  const url = `${API}/posts?${qs.toString()}`;
  const { data, res } = await fetchJson<WpPost[]>(url);
  const total = Number(res.headers.get("X-WP-Total") || 0);
  const totalPages = Number(res.headers.get("X-WP-TotalPages") || 0);
  const posts = data.map((post) => sanitizePost(post));
  return { posts, total, totalPages };
}

export async function getPostBySlug(slug: string) {
  return getPostBySlugLang(slug);
}

export async function getPostBySlugLang(slug: string, lang?: string) {
  const qs = new URLSearchParams({ slug, _embed: "1" });
  if (lang) {
    qs.set("lang", lang);
  }
  const url = `${API}/posts?${qs.toString()}`;
  const { data } = await fetchJson<WpPost[]>(url);
  const post = data?.[0];
  return post ? sanitizePost(post) : null;
}

export async function getPageBySlug(slug: string) {
  const qs = new URLSearchParams({ slug, _embed: "1", acf_format: "standard" });
  const url = `${API}/pages?${qs.toString()}`;
  const { data } = await fetchJson<WpPage[]>(url);
  const page = data?.[0];
  return page ? sanitizePage(page) : null;
}

export async function getPageById(id: number, lang?: string) {
  // Include ACF fields in standard format
  // Note: Adding aggressive cache-busting due to nginx/Cloudflare caching issues
  const qs = new URLSearchParams({
    _embed: "1",
    acf_format: "standard",
    // Add multiple cache-busting params to bypass different cache layers
    _t: Date.now().toString(),
    _nocache: Math.random().toString(36).substring(7),
  });

  // CRITICAL: Add lang parameter for Polylang to return correct language content
  if (lang) {
    qs.set("lang", lang);
  }

  const url = `${API}/pages/${id}?${qs.toString()}`;
  console.log(`[WP] Fetching page by ID ${id}${lang ? ` with lang=${lang}` : ''} with cache-busting params`);

  try {
    const { data } = await fetchJson<WpPage>(url);
    if (data) {
      console.log(`[WP] Successfully fetched page ID ${id}: "${data.title?.rendered}"`);

      // Log ACF data availability for debugging
      const acf = (data as any).acf;
      if (acf) {
        const layoutCount = acf.onp_layout ? (Array.isArray(acf.onp_layout) ? acf.onp_layout.length : 'not-array') : 'missing';
        console.log(`[WP] Page ${id} ACF data: onp_layout has ${layoutCount} sections`);
      } else {
        console.log(`[WP] Page ${id} has no ACF data`);
      }

      return sanitizePage(data);
    } else {
      console.warn(`[WP] Page ID ${id} returned no data`);
      return null;
    }
  } catch (error) {
    console.error(`[WP] Failed to fetch page by ID ${id}:`, error);
    return null;
  }
}

export async function getPageBySlugs(slugs: string[], lang?: string) {
  const ordered = slugs
    .map((slug) => slug.trim())
    .filter(Boolean);

  if (ordered.length === 0) return null;

  // IMPORTANT: WordPress/Polylang doesn't filter by lang parameter in REST API
  // Each language has its own page with its own slug
  // Don't pass lang parameter as it doesn't work with Polylang

  for (const slug of ordered) {
    const qs = new URLSearchParams({
      slug,
      _embed: "1",
      acf_format: "standard",
    });

    // DON'T add lang parameter - it doesn't work with Polylang
    // Each language page has its own unique slug

    const url = `${API}/pages?${qs.toString()}`;

    try {
      const { data } = await fetchJson<WpPage[]>(url);

      if (Array.isArray(data) && data.length > 0) {
        // Find exact slug match (WordPress might return partial matches)
        const exactMatch = data.find(page => page.slug === slug);
        if (exactMatch) {
          console.log(`[WP] Found page with slug "${slug}", ID: ${exactMatch.id}`);
          return sanitizePage(exactMatch);
        }
      }
    } catch (error) {
      // Log but continue trying other slugs
      console.warn(`[WP] Failed to fetch page with slug "${slug}":`, error);
    }
  }

  console.warn(`[WP] No page found for slugs: ${ordered.join(', ')}`);
  return null;
}

export async function getPageByPath(segments: string[], lang?: string) {
  const ordered = segments
    .map((segment) => segment.trim())
    .filter(Boolean);

  if (ordered.length === 0) return null;

  let parentId = 0;
  let resolved: WpPage | null = null;

  for (const slug of ordered) {
    const qs = new URLSearchParams({
      slug,
      parent: String(parentId),
      _embed: "1",
      acf_format: "standard",
    });
    if (lang) {
      qs.set("lang", lang);
    }

    const url = `${API}/pages?${qs.toString()}`;

    try {
      const { data } = await fetchJson<WpPage[]>(url);
      if (!Array.isArray(data) || data.length === 0) {
        return null;
      }

      // parent filter already applied; still prefer exact slug match
      const exactMatch = data.find((page) => page.slug === slug);
      const selected = sanitizePage(exactMatch ?? data[0]);
      resolved = selected;
      parentId = selected.id;
    } catch (error) {
      console.warn(`[WP] Failed to fetch page segment "${slug}" with parent ${parentId}:`, error);
      return null;
    }
  }

  return resolved;
}

type WpPreviewPayload = {
  id: number;
  title?: { rendered?: string };
  slug?: string;
  acf?: Record<string, unknown>;
};

export async function getPagePreviewById(id: number, nonce: string, lang?: string) {
  const qs = new URLSearchParams({ id: String(id), nonce });
  if (lang) {
    qs.set("lang", lang);
  }
  const base = typeof window !== "undefined" ? "" : wpBase;
  const url = `${base}/wp-json/onplexus/v1/preview?${qs.toString()}`;

  try {
    const { data } = await fetchJson<WpPreviewPayload>(url, {
      credentials: "include",
    });
    if (!data) return null;
    const preview: WpPage = sanitizePage({
      id: data.id,
      slug: data.slug ?? String(id),
      date: "",
      title: { rendered: data.title?.rendered ?? "" },
      content: { rendered: "" },
      acf: data.acf ?? {},
    });
    return preview;
  } catch (error) {
    if (error instanceof Error && /403/.test(error.message)) {
      throw new Error("Preview unavailable. Please verify your WordPress session.");
    }
    throw error;
  }
}

export async function getMenu(location: "header" | "footer", lang?: string) {
  const qs = new URLSearchParams({ location });
  if (lang) {
    qs.set("lang", lang);
  }
  const url = `${ONPLEXUS_API}/menu?${qs.toString()}`;
  const { data } = await fetchJson<{ items: MenuNode[] }>(url);
  return Array.isArray(data?.items) ? data.items : [];
}

export async function getTranslatedRoute(wpId: number, lang: string) {
  if (!wpId || !lang) return null;
  const qs = new URLSearchParams({ id: String(wpId), lang });
  const url = `${ONPLEXUS_API}/translate?${qs.toString()}`;
  const { data } = await fetchJson<{ found: boolean; route?: string }>(url);
  if (!data?.found) return null;
  return typeof data.route === "string" ? data.route : null;
}

export function toSpaHref(rawUrl: string, lang?: string) {
  if (!rawUrl) return rawUrl;
  const trimmed = rawUrl.trim();
  if (!trimmed) return rawUrl;
  if (trimmed.startsWith('#')) {
    return trimmed;
  }
  if (/^(mailto:|tel:)/i.test(trimmed)) {
    return trimmed;
  }

  const baseForUrl = isAbsolute
    ? wpBase
    : typeof window !== "undefined" && window.location
    ? window.location.origin
    : "http://localhost";

  let url: URL;
  try {
    url = new URL(trimmed, baseForUrl || "http://localhost");
  } catch {
    return trimmed;
  }

  const originMatch = WP_ORIGIN || (typeof baseForUrl === "string" ? (() => {
    try {
      return new URL(baseForUrl).origin;
    } catch {
      return null;
    }
  })() : null);

  const isInternal = trimmed.startsWith("/") || (originMatch ? url.origin === originMatch : false);
  if (!isInternal) {
    return trimmed;
  }

  let path = url.pathname;
  if (/\/\d{4}\/\d{2}\/\d{2}\//.test(path)) {
    const parts = path.split("/").filter(Boolean);
    const slug = parts.pop();
    if (slug) {
      path = `/blog/${slug}`;
    }
  }

  path = path.replace(/\/+/g, "/");
  if (path !== "/") {
    path = path.replace(/\/$/, "");
    if (path === "") {
      path = "/";
    }
  }

  if (lang && lang !== DEFAULT_LANG && !path.startsWith(`/${lang}`)) {
    path = path === "/" ? `/${lang}` : `/${lang}${path}`;
  }

  if ((!lang || lang === DEFAULT_LANG) && path.startsWith(`/${DEFAULT_LANG}/`)) {
    const stripped = path.slice(DEFAULT_LANG.length + 1);
    path = stripped ? `/${stripped}` : "/";
  }

  return path || "/";
}

function optimizeCfEmailScript(html: string): string {
  if (!html) return html;
  return html.replace(/<script[^>]*cloudflare-static\/email-decode\.min\.js[^>]*><\/script>/gi, () =>
    '<script data-cfasync="false" src="https://www.cloudflare.com/cdn-cgi/scripts/5c5dd728/cloudflare-static/email-decode.min.js" defer></script>'
  );
}

function sanitizeRenderedField(field: WpRendered): WpRendered {
  const cleaned = optimizeCfEmailScript(field.rendered || "");
  return cleaned === field.rendered ? field : { ...field, rendered: cleaned };
}

function sanitizeSeoHeadValue(value: string | null | undefined) {
  if (!value) return value;
  return optimizeCfEmailScript(value);
}

function sanitizePost(post: WpPost): WpPost {
  return {
    ...post,
    content: sanitizeRenderedField(post.content),
    excerpt: sanitizeRenderedField(post.excerpt),
    aioseo_head: sanitizeSeoHeadValue(post.aioseo_head),
  };
}

function sanitizePage(page: WpPage): WpPage {
  // Fix for page 281: WordPress returns acf as empty array [] instead of object
  const fixedPage = { ...page };
  if (Array.isArray(fixedPage.acf) && fixedPage.acf.length === 0) {
    // Convert empty array to empty object so downstream code doesn't break
    (fixedPage as any).acf = {};
  }

  return {
    ...fixedPage,
    content: sanitizeRenderedField(fixedPage.content),
    aioseo_head: sanitizeSeoHeadValue(fixedPage.aioseo_head),
  };
}

// ---------- Helpers ----------
export function getFeaturedImage(post: WpPost | WpPage, size?: string) {
  const media = (post._embedded?.["wp:featuredmedia"] || [])[0];
  if (!media) return undefined;
  if (size && media.media_details?.sizes?.[size]) {
    return media.media_details.sizes[size].source_url;
  }
  return media.source_url;
}
